import React, { useRef, useState } from "react";
import dayjs from "dayjs";
import { cn } from "utils";
import { OspIcons } from "@ospweb/sdk-ui/src/Icon/OspIcons";
import { DayPickerProps } from "react-day-picker";
import { DatePicker } from "@/src/components/DatePicker";
import { Popover } from "@/src/components/Popover";
import { EdgenModal } from "@/src/components/EdgenModal";
import { useIsPc } from "@/src/hooks";
import { PopoverRef } from "@/src/components/Popover/types";

export interface DateSelectProps {
  value?: Date;
  onChange?: (date: Date | undefined) => void;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
  disabledTime: DayPickerProps["disabled"];
}

export const DateSelect: React.FC<DateSelectProps> = ({
  value,
  onChange,
  className,
  placeholder = "Select Date",
  disabled = false,
  disabledTime,
}) => {
  const [isPc] = useIsPc();
  const [mobileOpen, setMobileOpen] = useState(false);
  const popoverRef = useRef<PopoverRef>(null);

  const handleConfirm = (date: Date | undefined) => {
    onChange?.(date);
    setMobileOpen(false);
    popoverRef?.current?.close();
  };

  const Trigger = (
    <div
      data-openreplay-label="DateSelect · DateSelect · div"
      className={cn(
        "flex items-center justify-between bg-background_solid_tertiary border border-border_white_transparent10 rounded-xl px-3 min-w-[200px] transition-colors",
        disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer",
        className,
      )}
      onClick={() => !disabled && !isPc && setMobileOpen(true)}
    >
      <span
        className={cn(
          "body_m",
          value ? "text-content_primary" : "text-content_secondary",
        )}
      >
        {value ? dayjs(value).format("MMM DD YYYY, HH:mm:ss") : placeholder}
      </span>
      <OspIcons
        name="calendar"
        size={20}
        className="text-content_secondary shrink-0 ml-2"
      />
    </div>
  );

  if (isPc) {
    if (disabled) {
      return Trigger;
    }
    return (
      <Popover
        ref={popoverRef}
        trigger="click"
        placement="bottom-start"
        contentClassName="p-0 bg-transparent shadow-none border-none"
        content={
          <div className="pt-2">
            <DatePicker
              selected={value}
              onConfirm={handleConfirm}
              showTime
              disabled={disabledTime}
            />
          </div>
        }
      >
        {Trigger}
      </Popover>
    );
  }

  return (
    <>
      {Trigger}
      {!disabled && (
        <EdgenModal
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          title="Select Date"
          zIndex={1000}
        >
          <div className="p-4 flex justify-center w-full">
            <DatePicker
              selected={value}
              onConfirm={handleConfirm}
              showTime
              disabled={disabledTime}
              classNames={{
                container:
                  "max-md:border-none w-full max-md:bg-transparent max-md:p-0 max-md:shadow-none",
                picker: "w-full",
                dayPicker: "flex-1",
              }}
            />
          </div>
        </EdgenModal>
      )}
    </>
  );
};
